import { get, keys } from 'lodash-es';
import { useEffect, useMemo, type MutableRefObject } from 'react';
import { useCallbackRef } from '../../hooks';
import { ModeEnum } from '../../types';
import { EMPTY_ARRAY } from '../../utils';
import {
  BlotterTableExtrasMenu,
  CollapseAllButton,
  ExpandAllButton,
  ExportMenuItems,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  type BlotterTableFiltersProps,
  type UseBlotterTable,
  type useGenericFilter,
} from '../BlotterTable';
import { Button, ButtonVariants, IconButton } from '../Button';
import { HStack } from '../Core';
import { Divider } from '../Divider';
import type { UseFilterBuilderOutput } from '../Filters';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import { EntityAdminNewEntityButton, EntityAdminPanelActionButtons } from './components/EntityAdminActionButtons';
import { useEntityAdminHeaderLayout } from './components/EntityAdminHeaderLayoutContext';
import type { EntityAdminClass } from './EntityAdminClass';
import { getEntityAdminDeletableEntities } from './getEntityAdminSelectionContextMenuItems';
import type { EntityAdminRecord } from './types';
import type { useEntityAdmin } from './useEntityAdmin';
import { isEntityAdminFilterablePropertiesLoading, resolveAllowAddEntity } from './utils';

export const useEntityAdminBlotterFilter = <TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>(
  props: Omit<ReturnType<typeof useEntityAdmin<TRecord, TDrawerRecord>>, 'renderDrawer'> &
    Pick<
      UseBlotterTable<EntityAdminClass<TRecord>>,
      | 'exportOptions'
      | 'exportDataAsCSV'
      | 'exportDataAsExcel'
      | 'blotterTableFiltersProps'
      | 'expandAllGroups'
      | 'collapseAllGroups'
      | 'gridApi'
    > & {
      openFilterClauseRef?: MutableRefObject<UseFilterBuilderOutput['addAndOpenClause'] | undefined>;
      filterResults: ReturnType<typeof useGenericFilter> & { controlledFilterKeys?: readonly string[] };
    }
) => {
  const {
    gridApi,
    exportOptions,
    exportDataAsCSV,
    exportDataAsExcel,
    blotterTableFiltersProps,
    allowBulkEdit,
    modeSwitchConfigurationInternal,
    allowEditEntity,
    allowDeleteEntity,
    selectedEntities,
    bulkEditFields,
    filterResults,
    openEntityAdminDrawer,
    updateClassEntitiesWithConfirmation,
    deleteClassEntitiesWithConfirmation,
    expandAllGroups,
    collapseAllGroups,
    childIDField,
    maxBulkEditRows,
    isRequesting,
    suppressBulkDelete,
    filtersPortalId,
    panelActions = EMPTY_ARRAY,
    entityName = 'Entity',
    createActionEntityName,
    allowAddEntity,
    drawerOptions,
    openFilterClauseRef,
    filterableProperties,
  } = props;
  const { isPanelLayout } = useEntityAdminHeaderLayout();
  const allowAddMain = resolveAllowAddEntity(allowAddEntity).main;

  const deletableEntities = useMemo(
    () => getEntityAdminDeletableEntities(selectedEntities, allowDeleteEntity).deletable,
    [selectedEntities, allowDeleteEntity]
  );

  const handleOnClickBulkEdit = useCallbackRef(() => {
    openEntityAdminDrawer({ setupEntities: selectedEntities, addingChildEntity: false });
  });

  const handleOnClickBulkModeSwitch = useCallbackRef((mode: ModeEnum) => {
    if (modeSwitchConfigurationInternal == null) {
      return;
    }
    updateClassEntitiesWithConfirmation(selectedEntities, {
      [modeSwitchConfigurationInternal.modeField]: mode,
    });
  });

  const handleOnClickBulkDelete = useCallbackRef(() => {
    deleteClassEntitiesWithConfirmation(deletableEntities);
  });

  const localFilterKeyCount = useMemo(() => {
    const controlledFilterKeys = new Set(filterResults.controlledFilterKeys ?? EMPTY_ARRAY);
    return keys(filterResults.filter).filter(key => !controlledFilterKeys.has(key)).length;
  }, [filterResults.controlledFilterKeys, filterResults.filter]);
  const hasControlledFilterKeys = (filterResults.controlledFilterKeys?.length ?? 0) > 0;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: {
      initialOpen: (props.initialFiltersOpen === true && !hasControlledFilterKeys) || localFilterKeyCount > 0,
    },
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  useEffect(() => {
    if (openFilterClauseRef == null) {
      return;
    }
    openFilterClauseRef.current = filterBuilderAccordion.openClause;
    return () => {
      openFilterClauseRef.current = undefined;
    };
  }, [filterBuilderAccordion.openClause, openFilterClauseRef]);

  const bulkEditDisabledDueToMaxRows = selectedEntities.length > maxBulkEditRows;
  const bulkEditDisabledDueToRequesting = isRequesting;

  const bulkEditPrefix = useMemo(() => {
    // Spaces / dealer panels keep these actions on the row context menu only.
    if (isPanelLayout) {
      return null;
    }

    const showBulkEditToolbar = allowBulkEdit === true && allowEditEntity !== false;
    // Bulk delete is an allowBulkEdit concern. allowBulkSelect is selection/copy only.
    const showBulkDelete =
      allowBulkEdit === true && allowDeleteEntity != null && allowDeleteEntity !== false && !suppressBulkDelete;

    if (!showBulkEditToolbar && !showBulkDelete) {
      return null;
    }

    const enableAllButtonDisabled =
      bulkEditDisabledDueToMaxRows ||
      bulkEditDisabledDueToRequesting ||
      modeSwitchConfigurationInternal == null ||
      selectedEntities.every(
        (entityClass: EntityAdminClass<TRecord>) =>
          (get(entityClass.data, modeSwitchConfigurationInternal.modeField) as ModeEnum) === ModeEnum.Enabled
      );
    const disableAllButtonDisabled =
      bulkEditDisabledDueToMaxRows ||
      bulkEditDisabledDueToRequesting ||
      modeSwitchConfigurationInternal == null ||
      selectedEntities.every(
        (entityClass: EntityAdminClass<TRecord>) =>
          (get(entityClass.data, modeSwitchConfigurationInternal.modeField) as ModeEnum) === ModeEnum.Disabled
      );
    const bulkEditSelectionButtonDisabled =
      bulkEditDisabledDueToMaxRows || bulkEditDisabledDueToRequesting || selectedEntities.length === 0;

    return (
      <HStack gap="spacing4">
        {showBulkEditToolbar && (
          <>
            <HStack gap="spacing4">
              {/* Show Bulk Edit button if any field other than Mode is allowed to be bulk edited.
              Mode editing is not performed from the drawer, but rather from the enable/disable buttons. */}
              {bulkEditFields != null && bulkEditFields.length >= 1 && (
                <Button
                  startIcon={IconName.Pencil}
                  size={FormControlSizes.Small}
                  disabled={bulkEditSelectionButtonDisabled}
                  variant={ButtonVariants.Primary}
                  onClick={() => handleOnClickBulkEdit()}
                  data-testid="bulk-edit-button"
                >
                  Bulk Edit Selection
                </Button>
              )}

              {(modeSwitchConfigurationInternal != null || showBulkDelete) && (
                <Divider orientation="vertical" m="spacing4" />
              )}
            </HStack>
            {modeSwitchConfigurationInternal != null && (
              <HStack gap="spacing4">
                <Button
                  startIcon={IconName.Check}
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Default}
                  disabled={enableAllButtonDisabled}
                  onClick={() => handleOnClickBulkModeSwitch(ModeEnum.Enabled)}
                  data-testid="entity-admin-enable-selected"
                >
                  Enable
                </Button>
                <Button
                  startIcon={IconName.Close}
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Default}
                  disabled={disableAllButtonDisabled}
                  onClick={() => handleOnClickBulkModeSwitch(ModeEnum.Disabled)}
                  data-testid="entity-admin-disable-selected"
                >
                  Disable
                </Button>
              </HStack>
            )}
          </>
        )}
        {showBulkDelete && (
          <>
            {showBulkEditToolbar && modeSwitchConfigurationInternal != null && (
              <Divider orientation="vertical" m="spacing4" />
            )}
            <IconButton
              icon={IconName.Trash}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Negative}
              disabled={deletableEntities.length === 0}
              onClick={() => handleOnClickBulkDelete()}
              data-testid="entity-admin-delete-selected"
            />
          </>
        )}
      </HStack>
    );
  }, [
    allowEditEntity,
    allowBulkEdit,
    allowDeleteEntity,
    modeSwitchConfigurationInternal,
    bulkEditDisabledDueToMaxRows,
    bulkEditDisabledDueToRequesting,
    bulkEditFields,
    handleOnClickBulkDelete,
    handleOnClickBulkEdit,
    handleOnClickBulkModeSwitch,
    selectedEntities,
    deletableEntities,
    suppressBulkDelete,
    isPanelLayout,
  ]);

  const collapseExpandPrefix = useMemo(() => {
    return childIDField ? (
      <HStack gap="spacing4">
        <ExpandAllButton onClick={expandAllGroups} data-testid="entity-admin-expand-all" />
        <CollapseAllButton onClick={collapseAllGroups} data-testid="entity-admin-collapse-all" />
      </HStack>
    ) : null;
  }, [childIDField, collapseAllGroups, expandAllGroups]);

  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const exportMenuSuffix = useMemo(() => {
    const showPanelActionsInExtras = isPanelLayout && (allowAddMain || panelActions.length > 0);
    if (!exportOptions && !showPanelActionsInExtras) {
      return null;
    }

    const newEntityButton =
      showPanelActionsInExtras && allowAddMain ? (
        <EntityAdminNewEntityButton
          entityName={entityName}
          createActionEntityName={createActionEntityName}
          disabled={drawerOptions == null}
          onClick={() => {
            extrasMenuPopover.close();
            openEntityAdminDrawer({ setupEntities: EMPTY_ARRAY, addingChildEntity: false });
          }}
        />
      ) : null;

    const panelActionButtons =
      showPanelActionsInExtras && panelActions.length > 0 ? (
        <EntityAdminPanelActionButtons
          panelActions={panelActions}
          honorVariant={false}
          onActionClick={extrasMenuPopover.close}
        />
      ) : null;

    const hasLeadingActions = newEntityButton != null || panelActionButtons != null;

    return (
      <BlotterTableExtrasMenu {...extrasMenuPopover}>
        {newEntityButton}
        {panelActionButtons}
        {hasLeadingActions && exportOptions != null && <Divider />}
        {exportOptions != null && (
          <ExportMenuItems
            blotterTable={{ exportOptions, exportDataAsCSV, exportDataAsExcel, gridApi }}
            handleClose={extrasMenuPopover.close}
            csvTestId="entity-admin-export-csv"
            excelTestId="entity-admin-export-excel"
          />
        )}
      </BlotterTableExtrasMenu>
    );
  }, [
    allowAddMain,
    createActionEntityName,
    drawerOptions,
    entityName,
    exportDataAsCSV,
    exportDataAsExcel,
    exportOptions,
    extrasMenuPopover,
    gridApi,
    isPanelLayout,
    openEntityAdminDrawer,
    panelActions,
  ]);

  const blotterTableFilter: BlotterTableFiltersProps = useMemo(() => {
    return {
      ...filterBuilderAccordion,
      ...blotterTableFiltersProps,
      quickFilterPrefix: bulkEditPrefix,
      prefix: collapseExpandPrefix,
      suffix: exportMenuSuffix,
      showFilterBuilder:
        isEntityAdminFilterablePropertiesLoading(filterableProperties) ||
        filterBuilderAccordion.filterBuilder.propertiesList.length > 0,
      portalId: filtersPortalId,
    };
  }, [
    filterBuilderAccordion,
    blotterTableFiltersProps,
    bulkEditPrefix,
    collapseExpandPrefix,
    exportMenuSuffix,
    filterableProperties,
    filtersPortalId,
  ]);

  const value = useMemo(() => {
    return blotterTableFilter;
  }, [blotterTableFilter]);

  return value;
};
